import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  LocalHospitalRounded,
  CheckCircleRounded,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { db } from 'services/firebase';
import { submitCheckIn } from 'services/clinicService';

/**
 * Public clinic check-in landing page (route: /checkin/:clinicId).
 *
 * The clinic name comes from the public `locations` doc. Checking in requires
 * sign-in; the actual write goes through the createCheckInVisit Cloud Function,
 * which records the visit under the patient's auth uid.
 *
 * Copy is in the visitStart namespace (clinicCheckIn). `error` holds a key from it.
 */
const CheckInPage = () => {
  const { clinicId } = useParams();
  const { user, userLoading } = useAuth();
  const { t } = useTranslation('visitStart');

  const [clinicName, setClinicName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInError, setCheckInError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clinicId) {
      setError('clinicCheckIn.missingClinic');
      setLoading(false);
      return undefined;
    }
    db.collection('locations')
      .doc(clinicId)
      .get()
      .then((snap) => {
        if (!active) return;
        if (!snap.exists) {
          setError('clinicCheckIn.notFound');
        } else {
          setClinicName(snap.data()?.title || '');
        }
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError('clinicCheckIn.loadFailed');
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clinicId]);

  const handleCheckIn = async () => {
    setCheckingIn(true);
    setCheckInError(null);
    try {
      await submitCheckIn(clinicId);
      setSuccess(true);
    } catch (err) {
      setCheckInError(err.message);
    } finally {
      setCheckingIn(false);
    }
  };

  // Without a title on the clinic's doc, the copy says "this clinic".
  const clinic = clinicName || t('clinicCheckIn.thisClinic');

  const renderBody = () => {
    if (loading || userLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">{t(error)}</Alert>;
    }

    if (success) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center">
          <CheckCircleRounded sx={{ fontSize: 56, color: 'success.main' }} />
          <Typography variant="h6">{t('clinicCheckIn.checkedInTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('clinicCheckIn.checkedInBody', { clinic })}
          </Typography>
          <Button component={RouterLink} to="/dashboard/messages" variant="contained">
            {t('clinicCheckIn.goToMessages')}
          </Button>
        </Stack>
      );
    }

    // An anonymous guest (they scanned the VisitStart code first) is not signed
    // in for this purpose: createCheckInVisit takes the patient id from the auth
    // context, so checking in as a guest files the visit under a throwaway uid and
    // puts it in the clinic's 30-day broadcast audience, where every message sent
    // to it goes nowhere.
    if (!user || user.isAnonymous) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography variant="body1">
            {t('clinicCheckIn.signInPrompt', { clinic })}
          </Typography>
          <Button component={RouterLink} to="/auth" variant="contained">
            {t('clinicCheckIn.signIn')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t('clinicCheckIn.signInAfter')}
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={2} alignItems="center" textAlign="center">
        <Typography variant="body1">
          <Trans
            ns="visitStart"
            i18nKey="clinicCheckIn.prompt"
            values={{ clinic }}
            components={{ 1: <strong /> }}
          />
        </Typography>
        {checkInError && (
          <Alert severity="error" sx={{ width: '100%' }} onClose={() => setCheckInError(null)}>
            {checkInError}
          </Alert>
        )}
        <LoadingButton
          variant="contained"
          size="large"
          loading={checkingIn}
          onClick={handleCheckIn}
        >
          {t('clinicCheckIn.submit')}
        </LoadingButton>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <LocalHospitalRounded sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700} textAlign="center">
            {t('clinicCheckIn.title')}
          </Typography>
        </Stack>
        {renderBody()}
      </Paper>
    </Box>
  );
};

export default CheckInPage;
